import {
  type ConditionOperators,
  WorkflowDisplayType,
  type WorkflowDisplayTypeValue
} from "./workflow.types";

/**
 * Which condition operators are meaningful on which stored `field.type` —
 * the SINGLE declaration, for the console and for every API write door alike.
 *
 * ── WHY THIS FILE EXISTS ────────────────────────────────────────────────────
 *
 * `CONDITION_OPERATORS` (workflow-condition.types.ts) answers *is this a real operator*.
 * It does not answer *is this operator meaningful for this field*, and the two
 * are different questions with different failure modes. A valid operator on the
 * wrong field type is not refused anywhere and does not throw: it returns a
 * CONSTANT. `not_empty` on a `number` field is `true` for every number including
 * `0`; `array_is_not_empty` on a `number` is `false` for every number. The branch
 * then takes one path for ever, the node reads back byte-identical to what was
 * sent, and no request, response, status code or log line says so.
 *
 * This table already existed and was already correct — it was `RuleByType` in
 * `apps/frontend/src/modules/Workflow/Nodes/Branching/Logic/contants.ts`, where
 * it restricted the console's operator picker. That is why the console is safe
 * and the API is not: the matrix was in the wrong PLACE, not missing. The
 * console's picker now reads THIS table, so there is still exactly one.
 *
 * 🚨 DO NOT WRITE A SECOND COPY OF THIS ANYWHERE — not in a validator, not in a
 * docs page, not in a CLI help table. Two matrices is the defect class this
 * lift exists to remove; import {@link isConditionOperatorCompatible} instead.
 *
 * ── HOW IT WAS CHECKED, AND AGAINST WHAT ────────────────────────────────────
 *
 * Derived BY EXECUTION, never by reading the evaluator: every one of the 19
 * operators was run through the real `evaluateCondition` against realistic field
 * values for each type, and each pair scored VARIES / ALWAYS / NEVER. All 25
 * pairs below score VARIES — measured 2026-08-17, zero disagreement. Validating
 * this table against the PICKER it came from would only have proved the copy was
 * faithful, including to a mistake.
 *
 * The negative controls from that same measurement, and they are the reason the
 * omissions below are omissions rather than oversights: `number x is_empty` is
 * NEVER, `number x not_empty` is ALWAYS, `string x array_is_empty` is NEVER.
 *
 * ── WHAT THIS TABLE IS NOT ──────────────────────────────────────────────────
 *
 * It is keyed by the DISPLAY type, because that is what a condition actually
 * stores: `Condition["field"]["type"]` is `WorkflowDisplayTypeValue`. The four
 * non-data display types carry an EMPTY list, which is a real answer — no
 * operator is meaningful against a branch, a null, or a type the builder has not
 * resolved yet — and not a gap awaiting entries.
 *
 * It says nothing about whether an operator EXISTS; `CONDITION_OPERATORS` owns
 * that, and a write door needs both checks. It also says nothing about the
 * VALUE: `needSecondOperand` records whether the operator reads a second operand
 * at all, so a unary operator arriving with a value is not a compatibility
 * failure.
 */
export type ConditionOperatorSpec = {
  readonly operator: ConditionOperators;
  /**
   * `false` for the unary operators — `is_empty`, `not_empty`, `array_is_empty`,
   * `array_is_not_empty` — which read `Condition["value"]` not at all.
   */
  readonly needSecondOperand: boolean;
};

export const CONDITION_OPERATORS_BY_FIELD_TYPE = {
  [WorkflowDisplayType.STRING]: [
    { operator: "equals", needSecondOperand: true },
    { operator: "not_equals", needSecondOperand: true },
    { operator: "contains", needSecondOperand: true },
    { operator: "not_contains", needSecondOperand: true },
    { operator: "starts_with", needSecondOperand: true },
    { operator: "ends_with", needSecondOperand: true },
    { operator: "is_empty", needSecondOperand: false },
    { operator: "not_empty", needSecondOperand: false }
  ],
  [WorkflowDisplayType.NUMBER]: [
    { operator: "equals", needSecondOperand: true },
    { operator: "not_equals", needSecondOperand: true },
    { operator: "greater_than", needSecondOperand: true },
    { operator: "less_than", needSecondOperand: true },
    { operator: "greater_equal", needSecondOperand: true },
    { operator: "less_equal", needSecondOperand: true }
  ],
  [WorkflowDisplayType.OBJECT]: [
    { operator: "has_key", needSecondOperand: true },
    { operator: "has_not_key", needSecondOperand: true },
    { operator: "is_empty", needSecondOperand: false },
    { operator: "not_empty", needSecondOperand: false }
  ],
  [WorkflowDisplayType.BOOLEAN]: [
    { operator: "equals", needSecondOperand: true },
    { operator: "not_equals", needSecondOperand: true }
  ],
  [WorkflowDisplayType.ARRAY]: [
    { operator: "array_is_empty", needSecondOperand: false },
    { operator: "array_is_not_empty", needSecondOperand: false },
    { operator: "array_has_length_greater_than", needSecondOperand: true },
    { operator: "array_has_length_less_than", needSecondOperand: true },
    { operator: "array_has_length_equal_to", needSecondOperand: true }
  ],
  [WorkflowDisplayType.VARIABLE]: [],
  [WorkflowDisplayType.NULL]: [],
  [WorkflowDisplayType.BRANCH]: [],
  [WorkflowDisplayType.UNKNOWN]: []
} as const satisfies Record<WorkflowDisplayTypeValue, readonly ConditionOperatorSpec[]>;

/**
 * The operators offered for a stored `field.type`, in the order the console
 * shows them. An unknown type answers an empty list rather than throwing —
 * `field.type` reaches this from a Prisma `Json` column and nothing validates it
 * on the way.
 */
export function conditionOperatorsForFieldType(
  fieldType: string | null | undefined
): readonly ConditionOperatorSpec[] {
  if (fieldType == null) return [];
  const row = (
    CONDITION_OPERATORS_BY_FIELD_TYPE as Record<
      string,
      readonly ConditionOperatorSpec[] | undefined
    >
  )[fieldType];
  return row ?? [];
}

/**
 * Whether `operator` is meaningful on a field of `fieldType`.
 *
 * FALSE covers three different things on purpose, because a write door refuses
 * all three the same way: the operator is real but constant on this type, the
 * type carries no operators at all, and the type is one nothing recognises.
 * Membership of the 19 is a SEPARATE check — an unknown operator is false here
 * too, but a caller that only runs this one reports the wrong reason.
 */
export function isConditionOperatorCompatible(
  operator: string | null | undefined,
  fieldType: string | null | undefined
): boolean {
  if (operator == null) return false;
  return conditionOperatorsForFieldType(fieldType).some((spec) => spec.operator === operator);
}

/**
 * Whether the pair reads `Condition["value"]` at all. Answers `false` for a pair
 * that is not compatible in the first place, so never use it as a compatibility
 * test — {@link isConditionOperatorCompatible} is that test.
 */
export function conditionOperatorNeedsSecondOperand(
  operator: string | null | undefined,
  fieldType: string | null | undefined
): boolean {
  if (operator == null) return false;
  return conditionOperatorsForFieldType(fieldType).some(
    (spec) => spec.operator === operator && spec.needSecondOperand
  );
}
